import { animate, style, transition, trigger } from '@angular/animations';
import { DatePipe, NgClass, NgIf } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterContentInit, Component, HostListener, Inject, ViewChild } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatDialog, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator, MatPaginatorSelectConfig, PageEvent } from '@angular/material/paginator';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TransformRunFrequency } from '../../../kyloe-pipes.pipe';
import { RippleDirective } from '../../../ripple.directive';
import { SkeletonComponent } from '../../../shared/skeleton/skeleton.component';
import { DataRulesConfirmDialogComponent } from '../../data-rules-confirm-dialog/data-rules-confirm-dialog.component';
import { IDataRulesTasks, TaskState } from '../../data-rules-model';
import { DataRulesService } from '../../data-rules.service';
import { ErrorModalComponent } from '../../error-modal/error-modal.component';
import { appearHeight } from '../../rule/kyloe-animations';

@Component({
  selector: 'app-tasks-dialog',
  templateUrl: './tasks-dialog.component.html',
  styleUrls: ['./tasks-dialog.component.scss', '../data-rules-history.component.scss'],
  imports: [
    SkeletonComponent,
    NgIf,
    MatDialogTitle,
    MatDialogContent,
    MatTooltip,
    MatTable,
    MatHeaderCell,
    MatCell,
    MatHeaderCellDef,
    MatCellDef,
    MatColumnDef,
    MatPaginator,
    MatDialogActions,
    MatButton,
    NgClass,
    RippleDirective,
    MatRowDef,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRow,
    TransformRunFrequency,
    DatePipe
  ],
  animations: [
    appearHeight,
    trigger('fadeButtonInOut', [
      transition(':enter', [
        style({ opacity: '0' }),
        animate('.2s ease', style({ opacity: '1' })),
      ]),
      transition(':leave', [
        style({ opacity: '1' }),
        animate('.2s ease', style({ opacity: '0' })),
      ]),
    ])
  ]
})
export class TasksDialogComponent implements AfterContentInit {
  innerDisplayedColumns: string[] =
      ['userId', 'userName', 'state', 'dateLastRun', 'updated', 'totalRecords', 'recordsProcessed', 'noUpdateRequired', 'recordsFailed', 'revert'];
  dataSource: MatTableDataSource<IDataRulesTasks> = new MatTableDataSource<IDataRulesTasks>([]);
  readonly pageSizeSelectConfig: MatPaginatorSelectConfig = { panelClass: 'kyloe-paginator-select-panel' };
  @ViewChild(MatPaginator) paginator: MatPaginator;
  tasks: IDataRulesTasks[];
  moreTasks: boolean;
  loading = true;
  skeletonBones: number;

  private runningTaskStates: TaskState[] = [TaskState.CANCELING,
                                            TaskState.POST_PROCESS,
                                            TaskState.PRE_PROCESS,
                                            TaskState.RUNNING,
                                            TaskState.STOPPING];

  protected readonly TaskState = TaskState;

  constructor(public dialogRef: MatDialogRef<TasksDialogComponent>,
              private dataRulesService: DataRulesService,
              private dialog: MatDialog,
              @Inject(MAT_DIALOG_DATA) public ruleData: {ruleId: string, name: string, timesRun: number}) {
    console.log(ruleData.ruleId);
    this.dataRulesService.getTaskPage(ruleData.ruleId, 5)
      .subscribe( (res: {moreResults: boolean, tasks: IDataRulesTasks[]}) => {
        console.log(res);

        this.paginator._intl.getRangeLabel = this.customRangeLabel;

        this.moreTasks = res.moreResults;
        this.tasks = res.tasks;
        this.dataSource.data = res.tasks;
        if (this.moreTasks) {
          this.paginator.length = 6;
        } else {
          this.paginator.length = res.tasks.length;
        }
        this.loading = false;
      });
  }

  // Keyboard Shortcuts
  // - Use Escape to close the dialog
  // Keyboard Shortcut - Use Esc to clear
  @HostListener('document:keydown', ['$event'])
  protected handleKeydownEvent(event: KeyboardEvent) {
    if (event.code === 'Escape') {
      this.dialogRef.close();
    }
  }

  onCancelClick(): void {
    this.dialogRef.close();
  }

  ngAfterContentInit(): void {
    this.dataSource.paginator = this.paginator;
    this.calcSkeletonBones();
  }

  stopTask(task: IDataRulesTasks) { // ME TOO - Could be task or task.instance
    task.state = TaskState.STOPPING;
    this.dataRulesService.stopTask(task).subscribe(_ => {
    }, error => {
      // this.showError('Failed to stop task.', error);
      task.state = TaskState.ERROR;
    });
  }

  resumeTask(task: IDataRulesTasks) { // ME TOO - Could be task or task.instance
    this.areTasksRunning().subscribe( tasksAreRunning => {
      if (tasksAreRunning) {
        this.dialog.open(DataRulesConfirmDialogComponent, {
          width: '55vw',
          height: 'auto',
          autoFocus: true,
          data: {
            titleIcon: 'DataRules-KyloeIcon',
            title: 'Task currently in progress',
            messageIcon: 'bhi-caution',
            message: 'Unable to resume task, as one is already in progress, please try again later',
            hideCancelButton: true,
            confirmButtonText: 'OK'
          }
        });
      } else {
        task.state = TaskState.RESUMING;
        this.dataRulesService.resumeTask(task).subscribe(() => {
        }, error => {
          task.state = TaskState.ERROR;
          this.showError('Failed to resume task.', error);
        });
      }
    });
  }

  revertTask(task: IDataRulesTasks) {
    this.areTasksRunning().subscribe( tasksAreRunning => {
      if (tasksAreRunning) {
        this.dialog.open(DataRulesConfirmDialogComponent, {
          width: '55vw',
          height: 'auto',
          autoFocus: true,
          data: {
            titleIcon: 'DataRules-KyloeIcon',
            title: 'Task currently in progress',
            messageIcon: 'bhi-caution',
            message: 'Unable to Revert this task, as a task is already in progress, please try again later',
            hideCancelButton: true,
            confirmButtonText: 'OK'
          }
        });
        return;
      }

      const dateLastRun = new Date(task.startTime);
      const dialogRef = this.dialog.open(DataRulesConfirmDialogComponent, {
        width: '75vw',
        height: 'auto',
        autoFocus: true,
        data: {
          titleIcon: 'History-KyloeIcon',
          title: 'Are you sure you want to revert this task?',
          messageIcon: 'bhi-refresh-o',
          message: `You are going to roll back <span class="red">${task.name}</span>, last run on <span class="red">${dateLastRun}</span>.
                  <br><br>This will return the field values to their previous state prior to this date.
                  This will also revert any changes that have been made to the affected fields within the entity record since the rule was last run.
                  <br><br>Are you sure you want to revert <span class="red">${task.totalRecords}</span> record(s)?`
        }
      });

      dialogRef.afterClosed().subscribe((data) => {
        if (!data)
          return;
        task.state = TaskState.ROLLING_BACK;
        this.dataRulesService.revertTask(task.sortKey, task.ruleId).subscribe(revertTask => {
            task = revertTask;
          },
          error => {
            task.state = TaskState.ERROR;
            this.showError('Failed to revert task.', error);
          });
      });
    });
  }

  pageChange(e: PageEvent) {
    const lowerLim: number = e.pageIndex * e.pageSize;
    const upperLim: number = (e.pageIndex + 1) * e.pageSize;

    if (this.moreTasks && upperLim >= this.tasks.length) {
      this.loading = true;
      this.paginator._intl.getRangeLabel = () => '...';
      this.dataRulesService.getTaskPage(this.ruleData.ruleId, e.pageSize, this.tasks[this.tasks.length - 1].sortKey)
        .subscribe( (nextPage) => {
          this.paginator._intl.getRangeLabel = this.customRangeLabel;
          this.moreTasks = nextPage.moreResults;
          this.tasks = this.tasks.concat(nextPage.tasks);
          this.dataSource.data = this.tasks.slice(lowerLim, upperLim);
          if (this.moreTasks) {
            this.paginator.length = this.tasks.length + 1; // TODO: bit of a hack.
          } else {
            this.paginator.length = this.tasks.length;
          }
          this.loading = false;
        });
    } else {
      this.dataSource.data = this.tasks.slice(e.pageIndex * e.pageSize, (e.pageIndex + 1) * e.pageSize);
    }
  }

  canRevert(task: IDataRulesTasks): boolean {
    return this.within30Days(task) && task.state === TaskState.COMPLETED && !task.isRollbackTask;
  }

  private calcSkeletonBones(): void {
    this.skeletonBones = Math.ceil(this.ruleData.timesRun / 2);
    if (this.ruleData.timesRun <= 2)
      this.skeletonBones = 1;
    if (this.ruleData.timesRun >= 5)
      this.skeletonBones = 3;
  }

  private within30Days(task: IDataRulesTasks): boolean {
    const MILLIS_PER_DAY = (1000 * 3600 * 24);
    const currentDate = new Date();
    let daysDifference = currentDate.getTime() - new Date(task.startTime).getTime();
    daysDifference = Math.ceil(daysDifference / MILLIS_PER_DAY);
    return daysDifference <= 30;
  }

  private areTasksRunning(): Observable<boolean> {
    return this.dataRulesService.getRuleStats().pipe(map ( stats => {
      const res = stats.some(stat => stat.state.toString() === 'RUNNING' || stat.state.toString() === 'ROLLING_BACK');
      console.log('tasks running?: ' + res);
      return res;
    }));
  }

  private areTaskQueued(): boolean {
    for (let i = 0; i < this.dataSource.data.length; i++) {
      if (this.dataSource.data[i].state === TaskState.QUEUED) {
        return true;
      }
    }
    return false;
  }

  private showError(message: string, cause?: any): void {
    console.error('KPDRH:', { message, cause });

    let theCause;
    if (cause instanceof HttpErrorResponse) {
      if (cause.error.error) {
        theCause = `${ cause.error.error }: ${ cause.error.message }`;
      } else if (cause.error.message) {
        theCause = cause.error.message;
      } else {
        theCause = cause.message;
      }
    }

    this.dialog.open(ErrorModalComponent, {
      height: 'auto',
      data: { title: 'Data Rules History', errorMessage: message, cause: theCause }
    });
  }

  private customRangeLabel(page: number, pageSize: number, length: number): string {
    return `${page * pageSize + 1} - ${Math.min((page + 1) * pageSize, length)}`;
  }
}
